import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { ScrollArea } from "../../ui/scroll-area";
import { Input } from "../../ui/input";
import { 
  Search, User, UserPlus, VolumeX, LogOut, Ban, Flag, MoreVertical 
} from 'lucide-react';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator 
} from '../../ui/dropdown-menu';

const getProfileUrl = (path) => {
  if (!path) return '/assets/default-avatar.png';
  if (path.startsWith('http')) return path;
  return `https://api.chathubindia.online${path}`; 
};

const MembersModal = (props) => { 
  console.log("🟢 MembersModal MOUNTED. Props:", props);
  const {
    open,
    onOpenChange,
    members = [],
    searchQuery,
    onSearchChange,
    user,
    onViewProfile,
    onAddFriend,
    onMuteUser,
    onKickUser,
    onBanUser,
    onBlockUser,
    onReportUser,
  } = props;

  const getMemberData = (member) => {
    const data = member.user || member; 
    return { 
      id: data.user_id || data.id || "unknown", 
      username: data.username || data.name || "Unknown User", 
      avatar: data.avatar_url || data.avatar || null, 
      role: (member.role || data.role || "member").toLowerCase(), 
      status: data.status || "active", 
      is_online: data.is_online || data.online || false 
    }; 
  };

  const filteredMembers = members.filter((rawMember) => { 
    const m = getMemberData(rawMember); 
    if (!searchQuery) return true; 
    const q = searchQuery.toLowerCase(); 
    return m.username.toLowerCase().includes(q) || m.role.includes(q); 
  });

  const handleViewProfile = (userId) => { 
    onOpenChange(false); 
    if (onViewProfile) {
      onViewProfile(userId);
    }
  };

  // Permission Helpers 
  const myRole = (user?.role || "").toLowerCase();
  const myUserId = user?.id;
  
  // Highest level of permission
  const isSuperAdmin = myRole === 'super_admin';
  // Staff can do most things, but not to other staff/super_admin
  const isStaff = myRole === 'staff' || isSuperAdmin;
  
  // Room-specific permissions
  const myRoomRole = (members.find(m => m.user_id === myUserId)?.role || 'member').toLowerCase();
  const isRoomOwner = myRoomRole === 'owner' || isSuperAdmin;
  const isRoomAdmin = myRoomRole === 'admin' || isRoomOwner;
  const isRoomMod = myRoomRole === 'moderator' || isRoomAdmin;

  // Function to determine if I can moderate a target user
  const canModerate = (targetMember) => {
    const targetRole = (targetMember.role || "member").toLowerCase();
    
    // Cannot moderate self
    if (targetMember.id === myUserId) return false;
    
    // Super Admins can moderate anyone except other Super Admins
    if (isSuperAdmin) return targetRole !== 'super_admin';
    
    // Staff can moderate anyone except other staff/super_admin
    if (isStaff) return !['staff', 'super_admin'].includes(targetRole);
    
    // Room Owners can moderate anyone in the room except staff/super_admin
    if (isRoomOwner) return !['staff', 'super_admin'].includes(targetRole);

    // Room Admins can moderate mods and members
    if (isRoomAdmin) return !['owner', 'admin', 'staff', 'super_admin'].includes(targetRole);

    // Room Mods can only moderate members
    if (isRoomMod) return targetRole === 'member';

    return false;
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <DialogHeader className="p-4 border-b">
          <DialogTitle>Room Members ({members.length})</DialogTitle>
        </DialogHeader>
        
        {/* Search Bar */}
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search members..."
              className="pl-9 bg-gray-50"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        <ScrollArea className="h-[400px] p-4">
          {filteredMembers.map((rawMember) => {
            const member = getMemberData(rawMember);
            const canTakeAction = canModerate(member);

            return (
              <div key={member.id} className="flex items-center justify-between p-2 mb-2 rounded-lg hover:bg-gray-50">
                <div 
                  className="flex items-center gap-3 cursor-pointer flex-grow"
                  onClick={() => handleViewProfile(member.id)}
                >
                  <div className="relative">
                    <img 
                      src={getProfileUrl(member.avatar)} 
                      alt={member.username} 
                      className="w-10 h-10 rounded-full object-cover border"
                      onError={(e) => e.target.src = '/assets/default-avatar.png'}
                    />
                    {member.is_online && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                    )}
                  </div>
                  
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 flex items-center gap-2">
                      {member.username}
                      {['owner', 'admin', 'moderator', 'staff', 'super_admin'].includes(member.role) && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">
                          {member.role}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">{member.is_online ? "Online" : "Offline"}</span>
                  </div>
                </div>

                { user && user.id !== member.id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="p-2 cursor-pointer">
                        <MoreVertical className="h-5 w-5 text-gray-500" />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 z-50">
                      <DropdownMenuLabel>Actions for {member.username}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem onClick={() => handleViewProfile(member.id)}>
                        <User className="mr-2 h-4 w-4" /> View Profile
                      </DropdownMenuItem>
                      
                      {onAddFriend && (
                        <DropdownMenuItem onClick={() => onAddFriend(member.id)}>
                          <UserPlus className="mr-2 h-4 w-4" /> Add Friend
                        </DropdownMenuItem>
                      )}

                      {canTakeAction && onMuteUser && (
                        <DropdownMenuItem onClick={() => onMuteUser(member)}>
                          <VolumeX className="mr-2 h-4 w-4" /> Mute User
                        </DropdownMenuItem>
                      )}

                      {canTakeAction && onKickUser && (
                        <DropdownMenuItem className="text-orange-600" onClick={() => onKickUser(member)}>
                          <LogOut className="mr-2 h-4 w-4" /> Kick User
                        </DropdownMenuItem>
                      )}
                      
                      {canTakeAction && onBanUser && (
                        <DropdownMenuItem className="text-red-600" onClick={() => onBanUser(member)}>
                          <Ban className="mr-2 h-4 w-4" /> Ban User
                        </DropdownMenuItem>
                      )}

                      {onBlockUser && (
                         <DropdownMenuItem className="text-red-600" onClick={() => onBlockUser(member.id)}>
                            <Ban className="mr-2 h-4 w-4" /> Block User
                        </DropdownMenuItem>
                      )}
                      
                      {onReportUser && (
                        <DropdownMenuItem onClick={() => onReportUser(member)}>
                          <Flag className="mr-2 h-4 w-4" /> Report User
                        </DropdownMenuItem>
                      )}

                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  ); 
};

export default MembersModal;